import { useState, useRef, useEffect } from "react";
import { BAROMETER_PRICES, PAKET_PRICES, KOMPLETT_EINZELSUMME, KOMPLETT_ERSPARNIS, WEBSITE_EINZELSUMME, WEBSITE_ERSPARNIS, MARKETING_EINZELSUMME, MARKETING_ERSPARNIS, formatPrice } from "@shared/prices";
import { BarometerFinder } from "@/components/BarometerFinder";
import { BarometerNav } from "@/components/BarometerNav";
import { CertBadges } from "@/components/CertBadges";
import { AgencyLogo } from "@/components/AgencyLogo";

const HERO_IMG = "https://d2xsxph8kpxj0f.cloudfront.net/310519663476281654/fDRgangGWFphFKzWtNAEav/larissa-hero_39f19908.jpeg";
const PORTRAIT_IMG = "https://d2xsxph8kpxj0f.cloudfront.net/310519663476281654/fDRgangGWFphFKzWtNAEav/larissa-portrait_abbef783.jpg";
const MEETERGO_URL = "https://cal.meetergo.com/larissa-girenko/30-min-meeting-or-larissa-girenko?utm_source=ig&utm_medium=social&utm_content=link_in_bio";

// ── Google-Bewertungen (verifiziert via Google Maps, Stand Mai 2026) ──
const GOOGLE_REVIEWS = [
  {
    name: "Nancy Glor (Portraitfotografin)",
    date: "vor 2 Tagen",
    stars: 5,
    text: "Ich habe bei Larissa eine Website-Analyse gebucht, um die Zusammenhänge zwischen SEO, GEO und der Sichtbarkeit meiner Website besser zu verstehen. Besonders beeindruckt hat mich ihre Fähigkeit, komplexe technische und strategische Themen sachlich zu analysieren und die Ergebnisse klar, verständlich und nachvollziehbar zu kommunizieren. Statt allgemeiner Empfehlungen erhielt ich konkrete Hinweise, nachvollziehbare Zusammenhänge und wertvolle Impulse für die Weiterentwicklung meiner Website. Die Analyse hat mir nicht nur gezeigt, wo Optimierungspotenziale liegen, sondern auch, welche Maßnahmen tatsächlich relevant sind und welche eher Zeit kosten als Nutzen bringen. Klare Empfehlung.",
    short: "Statt allgemeiner Empfehlungen erhielt ich konkrete Hinweise und nachvollziehbare Zusammenhänge. Die Analyse hat gezeigt, welche Maßnahmen wirklich zählen – klare Empfehlung.",
  },
  {
    name: "Lysann Neu",
    date: "vor einer Woche",
    stars: 5,
    text: "Ich habe bei Larissa eine Website-Analyse gebucht, bei der verschiedene Bereiche wie SEO-/GEO-Sichtbarkeit, Datenschutz, Conversion-Optimierung und weitere wichtige Aspekte analysiert wurden. Besonders hilfreich fand ich, dass Larissa als Außenstehende mit einem professionellen Blick auf meine Website geschaut hat. Dadurch konnte sie Potenziale erkennen, die mir selbst gar nicht bewusst waren. Am Ende habe ich konkrete und verständliche Handlungsempfehlungen erhalten, die ich direkt umsetzen kann. Besonders überzeugt hat mich Larissas umfangreiche Erfahrung im Online-Marketing, die man in ihrer Arbeit sofort merkt.",
    short: "Larissa hat Potenziale erkannt, die mir selbst nicht bewusst waren. Konkrete Handlungsempfehlungen, die ich direkt umsetzen kann.",
  },
  {
    name: "Jessica Jentzsch",
    date: "vor einer Woche",
    stars: 5,
    text: "Ich habe bereits an mehreren Webinaren und Workshops von Larissa teilgenommen und bin absolut begeistert. Larissa erklärt komplexe Themen unglaublich verständlich, klar strukturiert und mit vielen praxisnahen Beispielen. Man merkt sofort, dass sie ihr Fach wirklich beherrscht und mit viel Erfahrung arbeitet. Ich kann Larissa und ihre Werbeagentur von Herzen weiterempfehlen.",
    short: "Larissa erklärt komplexe Themen unglaublich verständlich. Man merkt sofort, dass sie ihr Fach wirklich beherrscht.",
  },
  {
    name: "Franziska Chrobot",
    date: "vor einer Woche",
    stars: 5,
    text: "Larissa ist eine echte Expertin auf ihrem Gebiet, unglaublich sympathisch und einfach eine smarte Unternehmerin, von der ich viel lernen durfte. Danke!",
    short: "Larissa ist eine echte Expertin auf ihrem Gebiet, unglaublich sympathisch und eine smarte Unternehmerin.",
  },
  {
    name: "Ines Thiele-Seidel",
    date: "vor 3 Wochen",
    stars: 5,
    text: "Ich arbeite schon seit längerer Zeit mit Larissa Girenko zusammen und könnte nicht zufriedener sein. Larissa bringt Klarheit ins Marketing. Keine leeren Versprechen, sondern ehrliche Analysen, durchdachte Strategien und Lösungen. Eine absolute Empfehlung für alle Selbstständigen und Kleinunternehmen.",
    short: "Larissa bringt Klarheit ins Marketing. Ehrliche Analysen, durchdachte Strategien. Absolute Empfehlung.",
  },
];

// ── Google-Bewertungen-Sektion ──
function GoogleReviewsSection() {
  return (
    <section className="py-14 px-6 bg-white border-t border-gray-100" aria-label="Google-Bewertungen WERBEAGENTUR Larissa Girenko">
      <div className="max-w-5xl mx-auto">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
          <div>
            <p className="text-xs font-bold tracking-widest uppercase mb-2" style={{ color: "#6d0000", fontFamily: "'DM Sans', sans-serif" }}>Verifizierte Bewertungen</p>
            <h2 className="font-black uppercase text-black leading-none" style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: "clamp(1.8rem, 3.5vw, 2.6rem)" }}>
              Was Kunden über Larissa sagen
            </h2>
          </div>
          {/* Google-Badge */}
          <a
            href="https://www.google.com/maps/place/WERBEAGENTUR+Larissa+Girenko"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-3 border border-gray-200 px-4 py-3 flex-shrink-0 hover:border-gray-400 transition-colors"
            aria-label="Alle Bewertungen auf Google Maps ansehen"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            <div>
              <div className="flex items-center gap-1">
                <span className="text-yellow-500 text-sm">★★★★★</span>
                <span className="font-black text-sm text-black" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>5,0</span>
              </div>
              <p className="text-xs text-gray-500" style={{ fontFamily: "'DM Sans', sans-serif" }}>5 Google-Bewertungen</p>
            </div>
          </a>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {GOOGLE_REVIEWS.map((review) => (
            <div key={review.name} className="flex flex-col border border-gray-200 p-6 bg-white relative">
              {/* Google-Icon oben rechts */}
              <div className="absolute top-4 right-4">
                <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
              </div>
              <div className="text-yellow-500 text-sm mb-3 tracking-wide">★★★★★</div>
              <p className="text-gray-600 text-sm leading-relaxed flex-1 mb-5" style={{ fontFamily: "'DM Sans', sans-serif" }}>
                &ldquo;{review.short}&rdquo;
              </p>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-black font-bold text-sm" style={{ fontFamily: "'DM Sans', sans-serif" }}>{review.name}</p>
                  <p className="text-gray-400 text-xs" style={{ fontFamily: "'DM Sans', sans-serif" }}>{review.date} · Google</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Link zu allen Bewertungen */}
        <div className="mt-6 text-center">
          <a
            href="https://www.google.com/maps/place/WERBEAGENTUR+Larissa+Girenko"
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs font-bold tracking-widest uppercase"
            style={{ color: "#6d0000", fontFamily: "'DM Sans', sans-serif" }}
          >
            Alle Bewertungen auf Google ansehen →
          </a>
        </div>
      </div>
    </section>
  );
}

const BAROMETER_COLORS = {
  sensorik:      "#6d0000",
  lokal:         "#6d0000",
  geo:           "#6d0000",
  seo:           "#6d0000",
  conversion:    "#6d0000",
  accessibility: "#6d0000",
  datenschutz:   "#6d0000",
  wettbewerb:    "#6d0000",
  ecommerce:     "#6d0000",
};

// ── Kompakte Barometer-Karte ──
interface BarometerCardProps {
  number: string;
  title: string;
  painPoint: string;
  tags: string[];
  href: string;
  accent: string;
  icon: string;
  price: string;
}

function BarometerCard({ number, title, painPoint, tags, href, accent, icon, price }: BarometerCardProps) {
  return (
    <a
      href={href}
      className="flex flex-col bg-white border border-gray-100 overflow-hidden group"
      style={{ transition: "box-shadow 0.18s, transform 0.18s" }}
      onMouseEnter={(e) => {
        e.currentTarget.style.boxShadow = "0 6px 24px rgba(0,0,0,0.10)";
        e.currentTarget.style.transform = "translateY(-2px)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.boxShadow = "none";
        e.currentTarget.style.transform = "translateY(0)";
      }}
    >
      <div className="h-0.5 w-full" style={{ backgroundColor: "#6d0000" }} />
      <div className="p-5 flex flex-col flex-1">
        <div className="flex items-center justify-between mb-3">
          {icon.startsWith('/') || icon.startsWith('http') ? (
            <img src={icon} alt="" width={28} height={28} style={{ width: 28, height: 28, objectFit: 'contain' }} />
          ) : (
            <span className="text-xl">{icon}</span>
          )}
          <span className="text-xs font-black text-gray-400" style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: "1.1rem" }}>{price}</span>
        </div>
        <h3 className="font-black uppercase leading-tight mb-2 text-black" style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: "1.25rem" }}>
          {title}
        </h3>
        <p className="text-xs text-gray-500 leading-relaxed mb-4 flex-1" style={{ fontFamily: "'DM Sans', sans-serif" }}>
          {painPoint}
        </p>
        <div className="flex flex-wrap gap-1 mb-4">
          {tags.slice(0, 3).map((tag) => (
            <span key={tag} className="text-xs px-2 py-0.5 border border-gray-200 text-gray-500 font-medium"
              style={{ fontFamily: "'DM Sans', sans-serif" }}>
              {tag}
            </span>
          ))}
        </div>
        <span className="block text-center font-bold text-xs uppercase tracking-widest px-4 py-3 border border-gray-200 text-gray-700"
          style={{ fontFamily: "'DM Sans', sans-serif" }}>
          Kostenlos testen →
        </span>
      </div>
    </a>
  );
}

// ── Pain-Points mit Scroll-Animation ──
function PainPointsSection() {
  const points = [
    "Ich investiere in Marketing – aber die Website bringt nichts",
    "Ich erhalte keine Anfragen oder Anrufe",
    "Ich werde bei Google nicht gefunden",
    "Besucher kommen, aber kaufen nicht",
    "Meine Seite wirkt nicht vertrauenswürdig",
    "Ich weiß nicht, was auf meiner Seite falsch läuft",
  ];
  const refs = useRef<(HTMLDivElement | null)[]>([]);
  useEffect(() => {
    const observers: IntersectionObserver[] = [];
    refs.current.forEach((el, i) => {
      if (!el) return;
      const obs = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            setTimeout(() => {
              el.style.opacity = "1";
              el.style.transform = "translateY(0)";
            }, i * 80);
            obs.disconnect();
          }
        },
        { threshold: 0.15 }
      );
      obs.observe(el);
      observers.push(obs);
    });
    return () => observers.forEach(o => o.disconnect());
  }, []);
  return (
    <section className="py-10 px-6 bg-white border-b border-gray-100" aria-label="Typische Probleme mit der Website">
      <div className="max-w-4xl mx-auto">
        <p className="text-xs font-bold tracking-widest uppercase mb-6 text-center" style={{ color: "#6d0000", fontFamily: "'DM Sans', sans-serif" }}>
          Klingt das bekannt?
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
          {points.map((point, i) => (
            <div
              key={point}
              ref={el => { refs.current[i] = el; }}
              className="flex items-start gap-3 px-4 py-3 border border-gray-200 bg-gray-50"
              style={{ opacity: 0, transform: "translateY(18px)", transition: "opacity 0.45s ease, transform 0.45s ease" }}
            >
              <span className="flex-shrink-0 font-bold text-sm mt-0.5" style={{ color: "#6d0000" }}>✗</span>
              <span className="text-gray-700 text-sm" style={{ fontFamily: "'DM Sans', sans-serif" }}>{point}</span>
            </div>
          ))}
        </div>
        {/* Social Proof */}
        <div className="mt-7 mb-6 text-center">
          <span className="text-yellow-500 text-base tracking-wide">★★★★★</span>
          <span className="text-gray-500 text-xs ml-2" style={{ fontFamily: "'DM Sans', sans-serif" }}>über 120 durchgeführte Analysen</span>
        </div>
        <div className="text-center">
          <a
            href="#barometer-finder"
            className="inline-block font-black uppercase tracking-widest text-sm px-8 py-4 text-white"
            style={{ backgroundColor: "#115751", fontFamily: "'Bebas Neue', sans-serif", fontSize: "1rem", letterSpacing: "0.12em" }}
            onMouseEnter={(e) => { (e.currentTarget as HTMLAnchorElement).style.backgroundColor = "#1a6b63"; }}
            onMouseLeave={(e) => { (e.currentTarget as HTMLAnchorElement).style.backgroundColor = "#115751"; }}
          >
            Welches Barometer passt zu mir? →
          </a>
        </div>
      </div>
    </section>
  );
}

// ── Marquee-Banner ──
function MarqueeBanner() {
  const [paused, setPaused] = useState(false);
  const items: { label: string; href?: string }[] = [
    { label: "✦ Sensorik-Barometer™", href: "/sensorik" },
    { label: "✦ Lokal-Barometer™", href: "/lokal" },
    { label: "✦ GEO-Barometer™", href: "/geo-barometer" },
    { label: "✦ SEO-Barometer™", href: "/seo-barometer" },
    { label: "✦ Conversion-Barometer™", href: "/conversion-barometer" },
    { label: "✦ Accessibility-Barometer™", href: "/accessibility-barometer" },
    { label: "✦ Datenschutz-Barometer™", href: "/datenschutz-barometer" },
    { label: "✦ Wettbewerb-Barometer™", href: "/wettbewerb-barometer" },
    { label: "✦ E-Commerce-Barometer™", href: "/ecommerce-barometer" },
    { label: "❖ Persönliche Analyse von Larissa" },
    { label: "❖ Lieferung in 72h" },
    { label: "✦ Larissa analysiert deine Website" },
    { label: "✦ Score (0–100)" },
    { label: "✦ Konkrete Handlungsempfehlungen" },
  ];
  return (
    <div
      className="w-full overflow-hidden bg-gray-100 py-3 cursor-pointer select-none border-y border-gray-200"
      aria-label="Vorteile – klicken zum Pausieren"
      title={paused ? "Klicken zum Fortsetzen" : "Klicken zum Pausieren"}
      onClick={() => setPaused(p => !p)}
    >
      <div style={{
        display: "flex",
        width: "max-content",
        animation: "marquee 40s linear infinite",
        animationPlayState: paused ? "paused" : "running",
      }}>
        {[...Array(2)].map((_, i) => (
          <div key={i} className="flex items-center" style={{ whiteSpace: "nowrap" }}>
            {items.map((item) => (
              item.href ? (
                <a
                  key={item.label}
                  href={item.href}
                  onClick={(e) => e.stopPropagation()}
                  className="text-gray-700 font-bold text-xs uppercase tracking-widest px-8 hover:text-[#6d0000] transition-colors"
                  style={{ fontFamily: "'DM Sans', sans-serif", textDecoration: "none" }}
                >
                  {item.label}
                </a>
              ) : (
                <span
                  key={item.label}
                  className="text-gray-500 font-bold text-xs uppercase tracking-widest px-8"
                  style={{ fontFamily: "'DM Sans', sans-serif" }}
                >
                  {item.label}
                </span>
              )
            ))}
          </div>
        ))}
      </div>
      <style>{`
        @keyframes marquee {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
      `}</style>
    </div>
  );
}

// ── FAQ-Item ──
function FaqItem({ question, answer }: { question: string; answer: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-gray-100">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between py-4 text-left"
        aria-expanded={open}
      >
        <span className="text-sm font-semibold text-black pr-4" style={{ fontFamily: "'DM Sans', sans-serif" }}>{question}</span>
        <span className="text-lg font-bold flex-shrink-0" style={{ color: "#6d0000" }}>{open ? "−" : "+"}</span>
      </button>
      {open && (
        <p className="text-sm text-gray-500 leading-relaxed pb-4" style={{ fontFamily: "'DM Sans', sans-serif" }}>
          {answer}
        </p>
      )}
    </div>
  );
}

export default function Home() {
  // SEO Meta-Tags
  useEffect(() => {
    document.title = "Website-Analyse & Google Ads Betreuung | WERBEAGENTUR Larissa Girenko";
    const setMeta = (name: string, content: string, prop = false) => {
      const attr = prop ? "property" : "name";
      let el = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement | null;
      if (!el) { el = document.createElement("meta"); el.setAttribute(attr, name); document.head.appendChild(el); }
      el.setAttribute("content", content);
    };
    setMeta("description", "Larissa Girenko analysiert deine Website persönlich: SEO, GEO, Conversion, Sensorik, Datenschutz & mehr. Score 0–100, PDF-Report in 72h. Für KMU & Selbstständige.");
    setMeta("keywords", "Website-Analyse, SEO-Analyse, Google Ads Betreuung, Conversion-Optimierung, GEO-Barometer, Larissa Girenko, Werbeagentur Leipzig");
    setMeta("og:title", "Website-Analyse & Google Ads | WERBEAGENTUR Larissa Girenko", true);
    setMeta("og:description", "Persönliche Website-Analyse: SEO, GEO, Conversion, Sensorik & mehr. Score 0–100, PDF-Report in 72h.", true);
    setMeta("og:url", "https://barometer.werbeagentur.me/", true);
    setMeta("og:type", "website", true);
    setMeta("og:image", "https://d2xsxph8kpxj0f.cloudfront.net/310519663476281654/fDRgangGWFphFKzWtNAEav/larissa-hero_39f19908.jpeg", true);
    setMeta("og:image:width", "1200", true);
    setMeta("og:image:height", "630", true);
    setMeta("twitter:card", "summary_large_image");
    setMeta("twitter:title", "Website-Analyse & Google Ads | WERBEAGENTUR Larissa Girenko");
    setMeta("twitter:description", "Persönliche Website-Analyse: SEO, GEO, Conversion, Sensorik & mehr. Score 0–100, PDF-Report in 72h.");
    // Canonical
    let canonical = document.querySelector("link[rel='canonical']") as HTMLLinkElement | null;
    if (!canonical) { canonical = document.createElement("link"); canonical.rel = "canonical"; document.head.appendChild(canonical); }
    canonical.href = "https://barometer.werbeagentur.me/";
    // hreflang
    let hreflang = document.querySelector("link[hreflang='de']") as HTMLLinkElement | null;
    if (!hreflang) { hreflang = document.createElement("link"); hreflang.rel = "alternate"; hreflang.setAttribute("hreflang", "de"); document.head.appendChild(hreflang); }
    hreflang.href = "https://barometer.werbeagentur.me/";
    // JSON-LD Organization
    const existingLd = document.getElementById("ld-home");
    if (existingLd) existingLd.remove();
    const ld = document.createElement("script");
    ld.id = "ld-home";
    ld.type = "application/ld+json";
    ld.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "ProfessionalService",
      "name": "WERBEAGENTUR Larissa Girenko",
      "url": "https://barometer.werbeagentur.me",
      "logo": "https://barometer.werbeagentur.me/manus-storage/logo_2d439bd1.jpg",
      "description": "Persönliche Website-Analysen für KMU: SEO, GEO, Conversion, Sensorik, Datenschutz & Google Ads Betreuung.",
      "address": { "@type": "PostalAddress", "streetAddress": "Lutherstr. 16 c", "addressLocality": "Leipzig", "postalCode": "04315", "addressCountry": "DE" },
      "email": "info@werbeagentur.me",
      "aggregateRating": { "@type": "AggregateRating", "ratingValue": "5.0", "reviewCount": "4", "bestRating": "5" }
    });
    document.head.appendChild(ld);
    return () => { document.getElementById("ld-home")?.remove(); };
  }, []);

  const barometers: BarometerCardProps[] = [
    {
      number: "01",
      title: "Sensorik-Barometer™",
      painPoint: "Warum kaufen Besucher nicht, obwohl sie auf deiner Seite sind? 95 % aller Kaufentscheidungen fallen unbewusst.",
      tags: ["Emotionen", "Vertrauen", "CTAs"],
      href: "/sensorik",
      accent: BAROMETER_COLORS.sensorik,
      icon: "/manus-storage/11_094992a3.svg",
      price: formatPrice(BAROMETER_PRICES.sensorik),
    },
    {
      number: "02",
      title: "Lokal-Barometer™",
      painPoint: "Warum findet dich niemand aus deiner Region, obwohl du dort arbeitest?",
      tags: ["Google Maps", "Lokale SEO", "Bewertungen"],
      href: "/lokal",
      accent: BAROMETER_COLORS.lokal,
      icon: "/manus-storage/1_55991aa3.svg",
      price: formatPrice(BAROMETER_PRICES.lokal),
    },
    {
      number: "03",
      title: "GEO-Barometer™",
      painPoint: "Wirst du in ChatGPT, Perplexity oder Google AI Overviews empfohlen?",
      tags: ["ChatGPT", "Perplexity", "E-E-A-T"],
      href: "/geo-barometer",
      accent: BAROMETER_COLORS.geo,
      icon: "/manus-storage/7_8bcad2d2.svg",
      price: formatPrice(BAROMETER_PRICES.geo),
    },
    {
      number: "04",
      title: "SEO-Barometer™",
      painPoint: "Verlierst du täglich Kunden an Wettbewerber, die bei Google weiter oben stehen?",
      tags: ["On-Page SEO", "Core Web Vitals", "PageSpeed"],
      href: "/seo-barometer",
      accent: BAROMETER_COLORS.seo,
      icon: "/manus-storage/23_1ab7147e.svg",
      price: formatPrice(BAROMETER_PRICES.seo),
    },
    {
      number: "05",
      title: "Conversion-Barometer™",
      painPoint: "Warum verlassen Besucher deine Seite, ohne zu kaufen oder anzufragen?",
      tags: ["Cialdini", "Preispsychologie", "Mobile UX"],
      href: "/conversion-barometer",
      accent: BAROMETER_COLORS.conversion,
      icon: "/manus-storage/33_d51b7819.svg",
      price: formatPrice(BAROMETER_PRICES.conversion),
    },
    {
      number: "06",
      title: "Accessibility-Barometer™",
      painPoint: "Schließt deine Website 20 % der Bevölkerung aus – und riskierst du Abmahnungen?",
      tags: ["WCAG 2.2", "EAA 2025", "ARIA"],
      href: "/accessibility-barometer",
      accent: BAROMETER_COLORS.accessibility,
      icon: "/manus-storage/16_9c3f7718.svg",
      price: formatPrice(BAROMETER_PRICES.accessibility),
    },
    {
      number: "07",
      title: "Datenschutz-Barometer™",
      painPoint: "Riskierst du Bußgelder bis 20 Mio. € – ohne es zu wissen?",
      tags: ["DSGVO", "Cookie-Consent", "SSL"],
      href: "/datenschutz-barometer",
      accent: BAROMETER_COLORS.datenschutz,
      icon: "/manus-storage/29_7f3bb394.svg",
      price: formatPrice(BAROMETER_PRICES.datenschutz),
    },
    {
      number: "08",
      title: "Wettbewerbs-Barometer™",
      painPoint: "Weißt du wirklich, warum Kunden zur Konkurrenz gehen statt zu dir?",
      tags: ["8 Dimensionen", "Benchmark", "Radar-Chart"],
      href: "/wettbewerb-barometer",
      accent: BAROMETER_COLORS.wettbewerb,
      icon: "/manus-storage/36_b604bbd9.svg",
      price: formatPrice(BAROMETER_PRICES.wettbewerb),
    },
    {
      number: "09",
      title: "E-Commerce-Barometer™",
      painPoint: "Verlässt dein Kunde den Warenkorb oder das Kontaktformular – ohne abzuschließen?",
      tags: ["Checkout", "Vertrauen", "Mobile Commerce"],
      href: "/ecommerce-barometer",
      accent: BAROMETER_COLORS.ecommerce,
      icon: "/manus-storage/21_453003f0.svg",
      price: formatPrice(BAROMETER_PRICES.ecommerce),
    },
  ];

  const faqItems = [
    {
      question: "Was ist ein Marketing-Barometer und wie funktioniert es?",
              answer: "Ein Marketing-Barometer ist ein spezialisiertes Analyse-Instrument, das deine Website anhand von 50–120+ Kriterien bewertet. Du gibst deine URL ein, Larissa analysiert persönlich und liefert Score (0–100) und konkrete Handlungsempfehlungen – kein automatisierter Report, sondern echte Expertise.",
    },
    {
      question: "Für wen sind die Barometer geeignet?",
      answer: "Für KMU, Selbstständige, Dienstleister und Online-Shops – alle, die eine Website haben und wissen wollen, warum sie nicht die gewünschten Ergebnisse erzielt. Kein technisches Vorwissen nötig.",
    },
    {
      question: "Was bekomme ich bei einer Barometer-Analyse?",
      answer: "Larissa analysiert deine Website persönlich: Score (0–100), alle Dimensionen, die wichtigsten Baustellen und priorisierte Maßnahmen. Du bekommst einen PDF-Report und weißt danach genau, was zu tun ist. Lieferung in 72h.",
    },
    {
      question: "Kann ich mehrere Barometer analysieren lassen?",
      answer: "Ja – du kannst mehrere Barometer buchen. Larissa kombiniert die Analysen und stellt dir ein individuelles Maßnahmenpaket zusammen.",
    },
    {
      question: "Wie lange dauert die Analyse?",
      answer: "Nach deiner Buchung analysiert Larissa deine Website persönlich und liefert den vollständigen PDF-Report innerhalb von 72 Stunden.",
    },
    {
      question: "Ersetzt das Datenschutz-Barometer eine Rechtsberatung?",
      answer: "Nein – es ist eine technische Analyse. Für rechtssichere Einschätzungen empfehlen wir, die Ergebnisse mit einem Fachanwalt zu besprechen.",
    },
  ];

  return (
    <div className="min-h-screen bg-white flex flex-col" style={{ fontFamily: "'DM Sans', sans-serif" }}>

      <BarometerNav current="/" />

      {/* ── HERO ── */}
      <section className="w-full bg-black overflow-hidden" aria-label="Marketing Barometer Suite – Website-Analyse für mehr Kunden">

        {/* Mobile: UX-optimiert – Bild rechts 40%, Text links, kompakt */}
        <div className="md:hidden bg-black relative" style={{ minHeight: "100svh" }}>
          {/* Bordeaux-Linie oben */}
          <div className="absolute top-0 left-0 right-0 h-0.5 z-10" style={{ backgroundColor: "#6d0000" }} />

          {/* Bild-Streifen rechts (40% Breite) – volle Höhe */}
          <div className="absolute top-0 right-0 bottom-0" style={{ width: "40%" }}>
            <img
              src={HERO_IMG}
              alt="Larissa Girenko"
              className="w-full h-full object-cover"
              style={{ objectPosition: "55% 18%" }}
            />
            <div className="absolute inset-0" style={{ background: "linear-gradient(to right, rgba(0,0,0,1) 0%, rgba(0,0,0,0.55) 35%, rgba(0,0,0,0.1) 100%)" }} />
          </div>

          {/* Text-Bereich – volle Breite, Padding rechts lässt Bild durchscheinen */}
          <div className="relative z-10 flex flex-col px-6 pt-8 pb-8 gap-6" style={{ minHeight: "100svh" }}>

            {/* Oben: Headline */}
            <div className="flex flex-col gap-4" style={{ maxWidth: "62%" }}>

              {/* Headline */}
              <h1 className="font-black uppercase leading-none text-white" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>
                <span style={{ fontSize: "clamp(2.8rem, 12vw, 3.8rem)", display: "block", lineHeight: 0.95 }}>Website-</span>
                <span style={{ fontSize: "clamp(2.8rem, 12vw, 3.8rem)", display: "block", lineHeight: 0.95 }}>Analyse</span>
                <span style={{ fontSize: "clamp(0.85rem, 3.2vw, 1rem)", color: "#d1d5db", display: "block", lineHeight: 1.4, fontWeight: 400, fontFamily: "'DM Sans', sans-serif", textTransform: "none", marginTop: "0.5rem" }}>mit der ersten Marketing Barometer Suite</span>
              </h1>

              {/* Claim – hell und gut lesbar auf Schwarz */}
              <p className="font-black uppercase leading-tight text-white" style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: "clamp(1.1rem, 4vw, 1.5rem)" }}>
                Mehr Klarheit.<br />Mehr Kunden.<br />Mehr Umsatz.
              </p>

              {/* Subtext – kurz & knapp */}
              <p className="text-gray-300 leading-relaxed" style={{ fontSize: "0.8rem", fontFamily: "'DM Sans', sans-serif" }}>
                Deine Website bringt keine Anfragen?{" "}
                <span style={{ color: "#f5f0eb", fontStyle: "italic", fontWeight: 600 }}>9 Analyse-Barometer</span>{" "}
                decken auf, warum – mit konkreten Handlungsempfehlungen.{" "}
                <span style={{ color: "#f5f0eb", fontStyle: "italic", fontWeight: 600 }}>Vorschau kostenlos.</span>
              </p>
            </div>

            {/* Unten: EIN primärer CTA – volle Breite für gute Tippfläche */}
            <div className="flex flex-col gap-3">
              <a
                href="#barometer-finder"
                className="flex items-center justify-center gap-2 text-white font-bold uppercase tracking-wider py-4 px-4 text-center"
                style={{ backgroundColor: "#115751", fontFamily: "'DM Sans', sans-serif", fontSize: "0.78rem", letterSpacing: "0.08em" }}
              >
                <span>Barometer wählen →</span>
              </a>
            </div>
            {/* Google-Bewertungen Mobile Hero */}
            <a
              href="https://www.google.com/maps/place/WERBEAGENTUR+Larissa+Girenko"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 mt-2"
              style={{ textDecoration: "none" }}
              aria-label="5,0 Sterne auf Google – 5 Bewertungen"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" aria-hidden="true" style={{ flexShrink: 0 }}>
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              <span style={{ color: "#FBBC05", fontSize: "0.7rem" }}>★★★★★</span>
              <span className="font-black text-white" style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: "0.85rem" }}>5,0</span>
              <span className="text-gray-400" style={{ fontSize: "0.65rem", fontFamily: "'DM Sans', sans-serif" }}>5 Google-Bewertungen</span>
            </a>
            {/* Zertifizierungen Mobile Hero – dezente Trust-Leiste */}
            <CertBadges showLabel={false} compact={true} className="mt-1" />
          </div>
        </div>

        {/* Desktop: Split-Layout */}
        <div className="hidden md:flex max-w-7xl mx-auto flex-row items-stretch" style={{ minHeight: "560px" }}>
          {/* Text */}
          <div className="flex-1 flex flex-col justify-center px-14 py-12">

            <h1 className="font-black uppercase leading-none mb-5 text-white" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>
              <span style={{ fontSize: "clamp(2.4rem, 5vw, 4.2rem)", display: "block", lineHeight: 1.05 }}>Website-Analyse</span>
              <span style={{ fontSize: "clamp(1rem, 1.8vw, 1.5rem)", color: "#ffffff", display: "block", lineHeight: 1.3, fontWeight: 400, fontFamily: "'DM Sans', sans-serif", textTransform: "none", letterSpacing: "0.01em", marginTop: "0.4rem" }}>mit der ersten Marketing Barometer Suite</span>
              {/* Claim in Weiß statt dunkelrot – gut lesbar auf Schwarz */}
              <span style={{ fontSize: "clamp(1.2rem, 2.2vw, 1.8rem)", color: "#ffffff", display: "block", lineHeight: 1.4, marginTop: "0.6rem" }}>
                Mehr Klarheit.<br />Mehr Kunden.<br />Mehr Umsatz.
              </span>
            </h1>
            <p className="text-gray-300 text-sm leading-relaxed mb-5 max-w-md" style={{ fontFamily: "'DM Sans', sans-serif" }}>
              Deine Website bringt keine Anfragen? <span style={{ color: "#f5f0eb", fontStyle: "italic", fontWeight: "600" }}>9 spezialisierte Analyse-Barometer</span> decken auf, warum – von Technik über Sichtbarkeit bis Psychologie. Mit konkreten Handlungsempfehlungen.
            </p>
            {/* EIN primärer CTA – Barometer-Finder */}
            <div style={{ maxWidth: "360px" }}>
              <a href="#barometer-finder"
                className="inline-block text-white font-bold text-sm uppercase tracking-widest px-8 py-4 text-center w-full"
                style={{ backgroundColor: "#115751", fontFamily: "'DM Sans', sans-serif" }}>
                Barometer wählen →
              </a>
              <p className="text-gray-400 text-xs mt-2" style={{ fontFamily: "'DM Sans', sans-serif" }}>9 spezialisierte Barometer · Persönlich von Larissa · 72h Lieferzeit</p>
            </div>
            {/* Google-Bewertungs-Badge im Hero */}
            <a
              href="https://www.google.com/maps/place/WERBEAGENTUR+Larissa+Girenko"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 mt-4 w-fit"
              style={{ textDecoration: "none" }}
              aria-label="5,0 Sterne auf Google – 5 Bewertungen"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true" style={{ flexShrink: 0 }}>
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              <div>
                <div className="flex items-center gap-1">
                  <span style={{ color: "#FBBC05", fontSize: "0.85rem", lineHeight: 1 }}>★★★★★</span>
                  <span className="font-black text-white" style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: "1rem" }}>5,0</span>
                </div>
                <p className="text-gray-400 text-xs" style={{ fontFamily: "'DM Sans', sans-serif" }}>4 verifizierte Google-Bewertungen</p>
              </div>
            </a>
            {/* Zertifizierungen Desktop – dezente Trust-Leiste */}
            <CertBadges showLabel={false} compact={true} className="mt-3" />
          </div>
          {/* Bild */}
          <div className="w-[44%] relative overflow-hidden">
            <img
              src={HERO_IMG}
              alt="Larissa Girenko – Expertin für Website-Analyse, SEO, GEO und Conversion-Optimierung"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0" style={{ background: "linear-gradient(to right, rgba(0,0,0,0.35) 0%, transparent 50%)" }} />
            <div className="absolute top-0 left-0 right-0 h-0.5" style={{ backgroundColor: "#6d0000" }} />
          </div>
        </div>
      </section>

      {/* ── Was ist eine Marketing Barometer Suite? ── */}
      <section className="py-16 px-6 bg-white border-b border-gray-100" aria-label="Was ist eine Marketing Barometer Suite">
        <div className="max-w-5xl mx-auto">
          {/* Headline */}
          <div className="text-center mb-12">
            <p className="text-xs font-bold tracking-widest uppercase mb-3" style={{ color: "#6d0000", fontFamily: "'DM Sans', sans-serif" }}>Das Konzept</p>
            <h2 className="font-black uppercase text-black mb-4" style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: "clamp(1.8rem, 3.5vw, 2.8rem)" }}>
              Was ist eine Marketing Barometer Suite?
            </h2>
            <p className="text-gray-500 max-w-2xl mx-auto text-sm leading-relaxed" style={{ fontFamily: "'DM Sans', sans-serif" }}>
              Ein Barometer misst unsichtbare Kräfte, die das Ergebnis bestimmen. Die Marketing Barometer Suite überträgt dieses Prinzip auf deine Website.
            </p>
          </div>

          {/* 3 Konzept-Kacheln */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
            {[
              {
                icon: "/manus-storage/32_04885248.svg",
                title: "Messen statt rätseln",
                text: "9 spezialisierte Analyse-Instrumente messen, was Besucher wirklich wahrnehmen, warum sie nicht kaufen und wo Google dich nicht findet.",
                accent: "#6d0000",
              },
              {
                icon: "/manus-storage/33_d51b7819.svg",
                title: "Score statt Bauchgefühl",
                text: "Jedes Barometer liefert einen klaren Score von 0–100 – objektiv, nachvollziehbar, vergleichbar.",
                accent: "#115751",
              },
              {
                icon: "/manus-storage/icon_onpage_grundlagen_a1ddd99a.png",
                title: "Maßnahmen statt Vermutungen",
                text: "Keine allgemeinen Tipps. Jeder Score kommt mit priorisierten, konkreten Handlungsempfehlungen für genau deine Website.",
                accent: "#1a3a5c",
              },
            ].map((item) => (
              <div key={item.title} className="border border-gray-100 p-6" style={{ borderTop: `3px solid ${item.accent}` }}>
                <img src={item.icon} alt="" className="w-10 h-10 mb-4 object-contain" style={{ opacity: 0.85 }} />
                <h3 className="font-black uppercase text-black mb-2" style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: "1.1rem" }}>{item.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed" style={{ fontFamily: "'DM Sans', sans-serif" }}>{item.text}</p>
              </div>
            ))}
          </div>

          {/* Analogie-Zitat */}
          <div className="border-l-4 pl-6 py-2" style={{ borderColor: "#6d0000" }}>
            <p className="text-gray-700 text-sm leading-relaxed italic" style={{ fontFamily: "'DM Sans', sans-serif" }}>
              „Wie ein Arzt nicht raten sollte, was dir fehlt – sondern messen – sollte auch dein Marketing auf Daten basieren, nicht auf Hoffnung."
            </p>
            <p className="text-xs font-bold tracking-widest uppercase mt-2" style={{ color: "#6d0000", fontFamily: "'DM Sans', sans-serif" }}>Larissa Girenko · Werbeagentur</p>
          </div>
        </div>
      </section>

      {/* ── Laufende Vorteils-Zeile ── */}
      <MarqueeBanner />

      {/* ── Pain-Points ── */}
      <PainPointsSection />

      {/* ── 3 Schritte ── */}
      <section className="py-12 px-6 bg-white border-b border-gray-100" aria-label="So funktioniert die Website-Analyse">
        <div className="max-w-4xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-0 divide-y md:divide-y-0 md:divide-x divide-gray-100">
            {[
              { step: "01", title: "Barometer wählen", desc: "Nutze den Finder oder wähle direkt eines der 9 spezialisierten Barometer – je nach deinem Ziel.", color: "#6d0000" },
              { step: "02", title: "URL eingeben", desc: "Kein Account, keine Installation. Einfach deine Website-URL eingeben und Analyse starten.", color: "#115751" },
              { step: "03", title: "Score & Maßnahmen", desc: "Score und priorisierte Handlungsempfehlungen in 30–90 Sekunden. Vollversion inkl. PDF-Report.", color: "#1a3a5c" },
            ].map((item) => (
              <div key={item.step} className="flex items-start gap-4 px-6 py-8 md:py-6">
                <span className="font-black text-3xl leading-none flex-shrink-0" style={{ color: `${item.color}25`, fontFamily: "'Bebas Neue', sans-serif" }}>{item.step}</span>
                <div>
                  <h3 className="font-black uppercase text-black mb-1" style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: "1.15rem" }}>{item.title}</h3>
                  <p className="text-gray-500 text-xs leading-relaxed" style={{ fontFamily: "'DM Sans', sans-serif" }}>{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Barometer-Finder ── */}
      <BarometerFinder />

      {/* ── So funktioniert die Analyse ── */}
      <section className="py-16 px-6 bg-white border-b border-gray-100" aria-label="So funktioniert die Barometer-Analyse">
        <div className="max-w-5xl mx-auto">
          <div className="mb-10 text-center">
            <p className="text-xs font-bold tracking-widest uppercase mb-2" style={{ color: "#6d0000", fontFamily: "'DM Sans', sans-serif" }}>Persönliche Analyse von Larissa</p>
            <h2 className="font-black uppercase text-black leading-none" style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: "clamp(2rem, 4vw, 3rem)" }}>
              Deine Website. Analysiert. Verbessert.
            </h2>
            <p className="text-gray-500 text-sm mt-3 max-w-lg mx-auto" style={{ fontFamily: "'DM Sans', sans-serif" }}>
              Wähle ein Barometer, gib deine URL ein und buche die Analyse direkt. Larissa liefert deinen persönlichen Report in 72h.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {/* Schritt 1 */}
            <div className="border border-gray-200 p-6 flex flex-col bg-white">
              <span className="font-black text-4xl mb-3" style={{ color: "#6d0000", fontFamily: "'Bebas Neue', sans-serif" }}>01</span>
              <h3 className="font-bold text-sm uppercase tracking-widest mb-2" style={{ fontFamily: "'DM Sans', sans-serif" }}>Barometer wählen</h3>
              <p className="text-sm text-gray-500 flex-1 mb-4" style={{ fontFamily: "'DM Sans', sans-serif" }}>Wähle das Barometer, das zu deinem größten Problem passt – SEO, Conversion, Sensorik, GEO oder mehr.</p>
              <a href="#barometer-finder"
                className="block text-center text-white font-bold text-xs uppercase tracking-widest py-3"
                style={{ backgroundColor: "#115751", fontFamily: "'DM Sans', sans-serif" }}>
                Barometer finden →
              </a>
            </div>

            {/* Schritt 2 */}
            <div className="border-2 p-6 flex flex-col relative" style={{ borderColor: "#6d0000", background: "#fff" }}>
              <div className="absolute -top-3.5 left-0 right-0 flex justify-center">
                <span className="px-4 py-1 text-xs font-black uppercase tracking-widest text-white" style={{ backgroundColor: "#6d0000", fontFamily: "'DM Sans', sans-serif" }}>
                  ★ Empfohlen
                </span>
              </div>
              <span className="font-black text-4xl mb-3 mt-2" style={{ color: "#6d0000", fontFamily: "'Bebas Neue', sans-serif" }}>02</span>
              <h3 className="font-bold text-sm uppercase tracking-widest mb-2" style={{ fontFamily: "'DM Sans', sans-serif" }}>URL eingeben & buchen</h3>
              <p className="text-sm text-gray-500 flex-1 mb-4" style={{ fontFamily: "'DM Sans', sans-serif" }}>Gib deine Website-URL ein und buche die Analyse direkt. Keine Registrierung nötig.</p>
              <div className="text-xs text-gray-400 border border-gray-100 px-3 py-2" style={{ fontFamily: "'DM Sans', sans-serif" }}>Kein Account · Direkte Buchung · Ab 97 €</div>
            </div>

            {/* Schritt 3 */}
            <div className="border border-gray-200 p-6 flex flex-col bg-white">
              <span className="font-black text-4xl mb-3" style={{ color: "#6d0000", fontFamily: "'Bebas Neue', sans-serif" }}>03</span>
              <h3 className="font-bold text-sm uppercase tracking-widest mb-2" style={{ fontFamily: "'DM Sans', sans-serif" }}>Vollständige Analyse kaufen</h3>
              <p className="text-sm text-gray-500 flex-1 mb-4" style={{ fontFamily: "'DM Sans', sans-serif" }}>Alle Befunde, priorisierte Empfehlungen und PDF-Report – von Larissa persönlich analysiert und geliefert in 72h.</p>
              <div className="text-xs text-gray-400 border border-gray-100 px-3 py-2" style={{ fontFamily: "'DM Sans', sans-serif" }}>Ab 97 € · PDF-Report · 72h Lieferzeit</div>
            </div>
          </div>

          {/* Haupt-CTA */}
          <div className="mt-8 text-center">
            <a href="#barometer-finder"
              className="inline-block text-white font-black uppercase tracking-widest px-12 py-4"
              style={{ backgroundColor: "#115751", fontFamily: "'Bebas Neue', sans-serif", fontSize: "1rem" }}>
              Jetzt Barometer wählen →
            </a>
            <p className="text-xs text-gray-400 mt-2" style={{ fontFamily: "'DM Sans', sans-serif" }}>Vollanalyse ab 97 € · Persönlich von Larissa · 72h Lieferzeit</p>
          </div>
        </div>
      </section>

      {/* ── 9 Barometer-Karten ── */}
      <main id="barometer" className="py-16 px-6 bg-[#f9f9f9]" aria-label="Alle 9 Marketing-Barometer im Überblick">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-10">
            <div>
              <p className="text-xs font-bold tracking-widest uppercase mb-2" style={{ color: "#6d0000", fontFamily: "'DM Sans', sans-serif" }}>
                9 spezialisierte Analyse-Tools
              </p>
              <h2 className="font-black uppercase text-black leading-none" style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: "clamp(1.8rem, 4vw, 2.8rem)" }}>
                Oder: Einzelnes Barometer wählen
              </h2>
            </div>
              <a href="#barometer-finder"
              className="inline-block text-white font-bold text-xs uppercase tracking-widest px-6 py-3 flex-shrink-0"
              style={{ backgroundColor: "#115751", fontFamily: "'DM Sans', sans-serif" }}>
              ✦ Barometer wählen →
            </a>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {barometers.map((b) => (
              <BarometerCard key={b.number} {...b} />
            ))}
          </div>
        </div>
      </main>

      {/* ── Über mich (kompakt) ── */}
      <section className="py-14 px-6 bg-white border-t border-gray-100" aria-label="Über Larissa Girenko">
        <div className="max-w-5xl mx-auto flex flex-col md:flex-row items-center gap-10">
          <div className="relative flex-shrink-0">
            <img
              src={PORTRAIT_IMG}
              alt="Larissa Girenko – Gründerin WERBEAGENTUR Larissa Girenko"
              className="object-cover"
              style={{ width: "200px", height: "260px", objectPosition: "top" }}
            />
            <div className="absolute top-0 left-0 right-0 h-0.5" style={{ backgroundColor: "#6d0000" }} />
          </div>
          <div className="flex-1">
            <p className="text-xs font-bold tracking-widest uppercase mb-3" style={{ color: "#6d0000", fontFamily: "'DM Sans', sans-serif" }}>
              WERBEAGENTUR Larissa Girenko
            </p>
            <h2 className="font-black uppercase leading-none mb-4 text-black" style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: "clamp(1.8rem, 3.5vw, 2.6rem)" }}>
              Dein Schlüssel zum Marketingerfolg
            </h2>
            <p className="text-gray-600 text-sm leading-relaxed mb-5 max-w-xl" style={{ fontFamily: "'DM Sans', sans-serif" }}>
              Du hast eine Website – aber sie bringt nicht die Ergebnisse, die du dir vorstellst? Genau dafür wurde die Marketing Barometer Suite entwickelt. Die Barometer machen unsichtbare Faktoren messbar: sensorische Wirkung, KI-Sichtbarkeit, lokale Präsenz, Conversion-Barrieren, technische SEO, Barrierefreiheit, Datenschutz, Wettbewerbs-Position und E-Commerce-Performance. Nicht als Bauchgefühl – als klarer Score mit konkreten Maßnahmen.
            </p>
            <a href="#barometer-finder"
              className="inline-block text-white font-bold text-sm uppercase tracking-widest px-8 py-3"
              style={{ backgroundColor: "#115751", fontFamily: "'DM Sans', sans-serif" }}>
              Barometer wählen →
            </a>
            {/* Badges unter CTA – Vertrauen vor Kauf */}
            <CertBadges showLabel={true} className="mt-4" />
          </div>
        </div>
      </section>

      {/* ── Google-Bewertungen (verifiziert) ── */}
      <GoogleReviewsSection />

      {/* Kundenstimmen-Sektion entfernt - GoogleReviewsSection oben enthält die echten Bewertungen */}
      {false && <section className="py-14 px-6 bg-[#f9f9f9] border-t border-gray-100" aria-label="Kundenstimmen">
        <div className="max-w-5xl mx-auto">
          <p className="text-xs font-bold tracking-widest uppercase mb-2 text-center" style={{ color: "#6d0000", fontFamily: "'DM Sans', sans-serif" }}>
            Was Kunden sagen
          </p>
          <h2 className="font-black uppercase text-black text-center mb-3 leading-none" style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: "clamp(1.8rem, 3.5vw, 2.6rem)" }}>
            Echte Ergebnisse. Klare Aussagen.
          </h2>
          {/* Google-Bewertungs-Header */}
          <div className="flex items-center justify-center gap-3 mb-8">
            <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            <span className="text-yellow-500 text-sm tracking-wide">★★★★★</span>
            <span className="font-black text-gray-800" style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: "1.1rem" }}>5,0</span>
            <span className="text-gray-400 text-xs" style={{ fontFamily: "'DM Sans', sans-serif" }}>· 5 Google-Bewertungen</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {/* Echte Google-Bewertung 1: Jessica Jentzsch */}
            <div className="flex flex-col border border-gray-200 p-6 bg-white">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-black flex-shrink-0" style={{ backgroundColor: "#6d0000", fontFamily: "'Bebas Neue', sans-serif" }}>J</div>
                  <div>
                    <p className="text-black font-bold text-xs" style={{ fontFamily: "'DM Sans', sans-serif" }}>Jessica Jentzsch</p>
                    <p className="text-gray-400 text-xs" style={{ fontFamily: "'DM Sans', sans-serif" }}>vor einer Woche</p>
                  </div>
                </div>
                <svg width="14" height="14" viewBox="0 0 24 24" aria-label="Google" style={{ flexShrink: 0 }}>
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
              </div>
              <div className="text-yellow-500 text-xs mb-3 tracking-wide">★★★★★</div>
              <p className="text-gray-600 text-sm leading-relaxed flex-1 mb-4" style={{ fontFamily: "'DM Sans', sans-serif" }}>
                &ldquo;Larissa erklärt komplexe Themen unglaublich verständlich, klar strukturiert und mit vielen praxisnahen Beispielen. Man merkt sofort, dass sie ihr Fach wirklich beherrscht. Ich kann Larissa und ihre Werbeagentur von Herzen weiterempfehlen.&rdquo;
              </p>
              <a
                href="https://www.google.com/maps/place/WERBEAGENTUR+Larissa+Girenko"
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs font-bold uppercase tracking-widest"
                style={{ color: "#6d0000", fontFamily: "'DM Sans', sans-serif", textDecoration: "none" }}
              >
                Auf Google lesen →
              </a>
            </div>
            {/* Echte Google-Bewertung 2: Franziska Chrobot */}
            <div className="flex flex-col border border-gray-200 p-6 bg-white">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-black flex-shrink-0" style={{ backgroundColor: "#115751", fontFamily: "'Bebas Neue', sans-serif" }}>F</div>
                  <div>
                    <p className="text-black font-bold text-xs" style={{ fontFamily: "'DM Sans', sans-serif" }}>Franziska Chrobot</p>
                    <p className="text-gray-400 text-xs" style={{ fontFamily: "'DM Sans', sans-serif" }}>vor einer Woche · Local Guide</p>
                  </div>
                </div>
                <svg width="14" height="14" viewBox="0 0 24 24" aria-label="Google" style={{ flexShrink: 0 }}>
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
              </div>
              <div className="text-yellow-500 text-xs mb-3 tracking-wide">★★★★★</div>
              <p className="text-gray-600 text-sm leading-relaxed flex-1 mb-4" style={{ fontFamily: "'DM Sans', sans-serif" }}>
                &ldquo;Larissa ist eine echte Expertin auf ihrem Gebiet, unglaublich sympathisch und einfach eine smarte Unternehmerin, von der ich viel lernen durfte. Danke!&rdquo;
              </p>
              <a
                href="https://www.google.com/maps/place/WERBEAGENTUR+Larissa+Girenko"
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs font-bold uppercase tracking-widest"
                style={{ color: "#6d0000", fontFamily: "'DM Sans', sans-serif", textDecoration: "none" }}
              >
                Auf Google lesen →
              </a>
            </div>
            {/* Echte Google-Bewertung 3: Ines Thiele-Seidel */}
            <div className="flex flex-col border border-gray-200 p-6 bg-white">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-black flex-shrink-0" style={{ backgroundColor: "#1a3a5c", fontFamily: "'Bebas Neue', sans-serif" }}>I</div>
                  <div>
                    <p className="text-black font-bold text-xs" style={{ fontFamily: "'DM Sans', sans-serif" }}>Ines Thiele-Seidel</p>
                    <p className="text-gray-400 text-xs" style={{ fontFamily: "'DM Sans', sans-serif" }}>vor 3 Wochen</p>
                  </div>
                </div>
                <svg width="14" height="14" viewBox="0 0 24 24" aria-label="Google" style={{ flexShrink: 0 }}>
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
              </div>
              <div className="text-yellow-500 text-xs mb-3 tracking-wide">★★★★★</div>
              <p className="text-gray-600 text-sm leading-relaxed flex-1 mb-4" style={{ fontFamily: "'DM Sans', sans-serif" }}>
                &ldquo;Larissa bringt Klarheit ins Marketing. Keine leeren Versprechen, sondern ehrliche Analysen, durchdachte Strategien und Lösungen, die wirklich zum eigenen Unternehmen passen. Eine absolute Empfehlung für alle Selbstständigen und Kleinunternehmen.&rdquo;
              </p>
              <a
                href="https://www.google.com/maps/place/WERBEAGENTUR+Larissa+Girenko"
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs font-bold uppercase tracking-widest"
                style={{ color: "#6d0000", fontFamily: "'DM Sans', sans-serif", textDecoration: "none" }}
              >
                Auf Google lesen →
              </a>
            </div>
          </div>

        </div>
      </section>}

      {/* ── FAQ ── */}
      <section className="py-14 px-6 bg-white border-t border-gray-100" aria-label="Häufige Fragen zur Marketing Barometer Suite">
        <div className="max-w-3xl mx-auto">
          <div className="mb-8">
            <p className="text-xs font-bold tracking-widest uppercase mb-2" style={{ color: "#6d0000", fontFamily: "'DM Sans', sans-serif" }}>
              Häufige Fragen
            </p>
            <h2 className="font-black uppercase text-black leading-none" style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: "clamp(1.6rem, 3.5vw, 2.4rem)" }}>
              FAQ – Marketing Barometer Suite
            </h2>
          </div>
          <div>
            {faqItems.map((item, i) => (
              <FaqItem key={i} question={item.question} answer={item.answer} />
            ))}
          </div>
        </div>
      </section>

      {/* ── Finaler CTA – EIN Button ── */}
      <section className="py-14 px-6 text-center" style={{ backgroundColor: "#115751" }} aria-label="Jetzt Barometer wählen">
        <div className="max-w-xl mx-auto">
          <h2 className="font-black uppercase text-white mb-3" style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: "clamp(2rem, 5vw, 3.2rem)" }}>
            Welches Barometer passt zu dir?
          </h2>
          <p className="text-sm leading-relaxed mb-6" style={{ color: "rgba(255,255,255,0.75)", fontFamily: "'DM Sans', sans-serif" }}>
            Vollanalyse ab 97 € · Persönlich von Larissa analysiert · Lieferung in 72h
          </p>
          <a href="#barometer-finder"
            className="inline-block bg-white font-black uppercase tracking-widest px-10 py-4"
            style={{ color: "#115751", fontFamily: "'Bebas Neue', sans-serif", fontSize: "1rem" }}>
            Barometer wählen →
          </a>
        </div>
      </section>

      {/* ── Footer ── */}
      <footer className="border-t border-gray-100 px-6 py-8 bg-white">
        {/* Badges im Footer – letzter Vertrauensanker */}
        <div className="max-w-5xl mx-auto mb-6 flex justify-center">
          <CertBadges showLabel={true} />
        </div>
        <div className="max-w-5xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center">
            <AgencyLogo withLogo size="sm" />
          </div>
          <nav className="flex flex-wrap items-center gap-4 justify-center" aria-label="Footer-Navigation">
            {[
              { href: "/sensorik", label: "Sensorik" },
              { href: "/lokal", label: "Lokal" },
              { href: "/geo-barometer", label: "GEO" },
              { href: "/seo-barometer", label: "SEO" },
              { href: "/conversion-barometer", label: "Conversion" },
              { href: "/accessibility-barometer", label: "Accessibility" },
              { href: "/datenschutz-barometer", label: "Datenschutz" },
              { href: "/wettbewerb-barometer", label: "Wettbewerb" },
              { href: "/ecommerce-barometer", label: "E-Commerce" },
              { href: "/google-ads", label: "Google Ads" },
              { href: "/impressum", label: "Impressum" },
              { href: "/datenschutz", label: "Datenschutzerklärung" },
            ].map((item) => (
              <a key={item.href} href={item.href} className="text-xs text-gray-400 hover:text-gray-700 tracking-widest uppercase"
                style={{ fontFamily: "'DM Sans', sans-serif" }}>
                {item.label}
              </a>
            ))}
          </nav>
          <p className="text-xs text-gray-400" style={{ fontFamily: "'DM Sans', sans-serif" }}>© 2026 WERBEAGENTUR Larissa Girenko</p>
        </div>
        <div className="text-center mt-4">
          <p className="text-xs text-gray-300" style={{ fontFamily: "'DM Sans', sans-serif" }}>
            Marketing Barometer Suite™ · SEO · GEO · Conversion · Sensorik · Lokal · Accessibility · Datenschutz · Wettbewerb · E-Commerce
          </p>
        </div>
      </footer>
    </div>
  );
}
